import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserEntity } from '../../../../domain/entities/user/userEntity';
import type { AuthState } from '@/domain/entities/auth/authState';
import type { RootState } from '@/presentation/store';
import { clearImageCache } from '../../../components/common/CachedImage';
import { appLogger } from '@/domain/services/analytics/appLogger';

const initialState: AuthState = {
  firebaseUserId: null,
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isInitializing: true, // App starts initializing; AppInitializer (or LoginPage) flips this to false once storage + Redux are ready
  error: null,
  redirectTo: null,
  autoLoggedOut: false,
  chattingWith: '', // Firebase ID of the peer the current user is actively chatting with (mirrors Flutter CurrentUserNotifier.user.chattingWith, chat_screen.dart:312-334). Local Redux only.
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setFirebaseUserId: (state, action: PayloadAction<string | null>) => {
      state.firebaseUserId = action.payload;
      state.isAuthenticated = !!action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setUser: (state, action: PayloadAction<UserEntity | null>) => {
      state.user = action.payload;
      // Note: Don't update isAuthenticated here - setFirebaseUserId handles that
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setInitializing: (state, action: PayloadAction<boolean>) => {
      state.isInitializing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    logout: (state) => {
      state.firebaseUserId = null;
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isInitializing = false;
      state.error = null;
      state.redirectTo = null;
      
      // Clear image cache on logout for privacy
      clearImageCache().catch(error => {
        appLogger.error('Failed to clear image cache on logout', 'AuthSlice.logout', { error: error as Error });
      });
    },
    setRedirectTo: (state, action: PayloadAction<string | null>) => {
      state.redirectTo = action.payload;
    },
    clearRedirectTo: (state) => {
      state.redirectTo = null;
    },
    setAutoLoggedOut: (state, action: PayloadAction<boolean>) => {
      state.autoLoggedOut = action.payload;
    },
    setChattingWith: (state, action: PayloadAction<string>) => {
      state.chattingWith = action.payload;
    },
  },
});

export const { setFirebaseUserId, setUser, setLoading, setInitializing, setError, logout, setRedirectTo, clearRedirectTo, setAutoLoggedOut, setChattingWith } = authSlice.actions;
export default authSlice.reducer;

// --- Selectors ---

export const selectChattingWith = (state: RootState): string =>
  state.auth.chattingWith;